"use client";

import { Link } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Zap, Shield, Globe, Code, ArrowRight, Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const modelList = [
  { name: "Claude Sonnet 4.5", provider: "Anthropic", badge: "Popular" },
  { name: "Claude Haiku 3.5", provider: "Anthropic", badge: "Fast" },
  { name: "GPT-4o", provider: "OpenAI", badge: "Coming" },
  { name: "Gemini 2.5 Pro", provider: "Google", badge: "Coming" },
  { name: "Grok", provider: "xAI", badge: "Coming" },
  { name: "DeepSeek V3", provider: "DeepSeek", badge: "Coming" },
];

export default function LandingPage() {
  const t = useTranslations("landing");
  const tNav = useTranslations("nav");

  const tiers = [
    {
      key: "free" as const,
      price: "Rp 0",
      features: t.raw("features_free") as string[],
      highlight: false,
    },
    {
      key: "pro" as const,
      price: "Rp 149.000",
      features: t.raw("features_pro") as string[],
      highlight: true,
    },
    {
      key: "enterprise" as const,
      price: "Rp 499.000",
      features: t.raw("features_enterprise") as string[],
      highlight: false,
    },
  ];

  const features = [
    { icon: Zap, key: "unified" as const },
    { icon: Shield, key: "secure" as const },
    { icon: Globe, key: "payment" as const },
    { icon: Code, key: "dev" as const },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="border-b sticky top-0 bg-background/95 backdrop-blur z-50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="text-xl font-bold">
            <span className="text-primary">Ethan</span> AI
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link href="/auth/login">
              <Button variant="ghost">{tNav("login")}</Button>
            </Link>
            <Link href="/auth/register">
              <Button>{tNav("getStarted")}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge variant="secondary" className="mb-4">{t("badge")}</Badge>
          <h1 className="text-5xl font-bold tracking-tight mb-6">
            {t("hero")}
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            {t("heroSub")}
          </p>
          <div className="flex gap-4 justify-center mb-12">
            <Link href="/auth/register">
              <Button size="lg" className="gap-2">
                <Sparkles className="w-4 h-4" /> {t("startFree")} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link href="/playground">
              <Button size="lg" variant="outline">{t("tryPlayground")}</Button>
            </Link>
          </div>

          {/* Code example */}
          <div className="bg-zinc-950 text-zinc-100 rounded-lg p-6 text-left font-mono text-sm max-w-2xl mx-auto">
            <div className="text-zinc-500 mb-2">{t("codeComment")}</div>
            <div>
              <span className="text-green-400">curl</span> https://ethan.blocku.tech/v1/chat/completions \
            </div>
            <div className="pl-4">-H <span className="text-yellow-400">&quot;Authorization: Bearer sk-eth-xxxxx&quot;</span> \</div>
            <div className="pl-4">-d <span className="text-yellow-400">&apos;&#123;&quot;model&quot;: &quot;claude-sonnet-4-5&quot;, &quot;messages&quot;: [...]&#125;&apos;</span></div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">{t("featuresTitle")}</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {features.map((f) => (
              <Card key={f.key}>
                <CardHeader>
                  <f.icon className="w-8 h-8 text-primary mb-2" />
                  <CardTitle className="text-lg">{t(`features.${f.key}.title`)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">{t(`features.${f.key}.desc`)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Models */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">{t("modelsTitle")}</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {modelList.map((m) => (
              <Card key={m.name} className="flex items-center justify-between p-4">
                <div>
                  <p className="font-medium">{m.name}</p>
                  <p className="text-sm text-muted-foreground">{m.provider}</p>
                </div>
                <Badge variant={m.badge === "Coming" ? "outline" : "default"}>
                  {t(`badges.${m.badge}`)}
                </Badge>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">{t("pricingTitle")}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier) => (
              <Card key={tier.key} className={tier.highlight ? "border-primary shadow-lg" : ""}>
                <CardHeader>
                  {tier.highlight && <Badge className="w-fit mb-2">{t("mostPopular")}</Badge>}
                  <CardTitle>{t(`tiers.${tier.key}.name`)}</CardTitle>
                  <div className="text-3xl font-bold">
                    {tier.price}
                    <span className="text-base font-normal text-muted-foreground">
                      {t(`tiers.${tier.key}.period`)}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {tier.features.map((f) => (
                      <li key={f} className="text-sm flex items-center gap-2">
                        <span className="text-primary">&#10003;</span> {f}
                      </li>
                    ))}
                  </ul>
                  <Link href="/auth/register">
                    <Button className="w-full" variant={tier.highlight ? "default" : "outline"}>
                      {t(`tiers.${tier.key}.cta`)}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8 px-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center text-sm text-muted-foreground">
          <p>{t("footer")}</p>
          <p>{t("footerCompany")}</p>
        </div>
      </footer>
    </div>
  );
}
